import { infiniteQueryOptions } from '@tanstack/react-query';
import type { HevitonApiV2, RawPageNode } from '@heviton/heviton-api-v2';
import { fromRawSubscriptionList } from '@/shared/data-mappers';
import type { SubscriptionListItem } from '@/shared/data-types';

/**
 * 발전소 목록 + 구독 상태 query 정의 SSOT. BE = v2 `GET /plants?include=subscription` (미구독 포함).
 * 서버 페이지네이션(page/size, Spring PageImpl) → `useInfiniteQuery` + 하단 [더보기] 로 페이지 누적.
 * plant-first 인 이유: 화면이 미구독 발전소도 포함하는데 `GET /subscriptions` 는 구독건만 반환한다.
 * page base 0-base(요청·응답 `pageInfo.page` 모두) — 다음 페이지는 echo 된 page 가 아니라 누적 개수 < totalElements 로 판단.
 */
export const SUBSCRIPTIONS_QUERY_KEY = ['subscriptions'] as const;

/** 페이지 크기 — BE 기본값(10)과 정합. */
const PAGE_SIZE = 10;

/** infinite query 의 페이지 단위 — 매핑된 행 + 다음 페이지 판단용 pageInfo. */
export interface SubscriptionsPage {
  items: SubscriptionListItem[];
  pageInfo?: RawPageNode;
}

export const subscriptionsQueryOptions = (api: HevitonApiV2) =>
  infiniteQueryOptions({
    queryKey: SUBSCRIPTIONS_QUERY_KEY,
    initialPageParam: 0,
    queryFn: async ({ pageParam, signal }): Promise<SubscriptionsPage> => {
      const raw = await api.plant.list(
        { include: 'subscription,order', page: pageParam, size: PAGE_SIZE },
        { signal },
      );
      return { items: fromRawSubscriptionList(raw), pageInfo: raw.pageInfo };
    },
    // 요청·응답 page 모두 0-base. echo 를 신뢰하지 않고 누적 개수로 다음 페이지 판단.
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.pageInfo?.totalElements ?? 0;
      const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < total ? allPages.length : undefined;
    },
  });
